import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Library as LibraryIcon, Download, AlertTriangle, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function PublicCaptionLibraryPage() {
  const { shareId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [lib, setLib] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    api.get(`/public/caption-libraries/${shareId}`)
      .then(({ data }) => setLib(data))
      .catch((e) => setError(e.response?.data?.detail || "Library not found"));
  }, [shareId]);

  const importToMine = async () => {
    if (!user) {
      nav(`/login?redirect=${encodeURIComponent(`/share/library/${shareId}`)}`);
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post(`/public/caption-libraries/${shareId}/import`);
      toast.success(`Added “${data.name}” to your libraries`);
      nav("/caption-libraries");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h1 className="text-2xl font-display font-bold mb-2">Library unavailable</h1>
          <p className="text-zinc-400 text-sm mb-5">{error}</p>
          <Button onClick={() => nav("/dashboard")} className="bg-blue-600 hover:bg-blue-500">
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }
  if (!lib) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-500 grid place-items-center">Loading…</div>;
  }

  const vars = Object.entries(lib.variables || {});

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <header className="border-b border-zinc-800 px-6 h-14 flex items-center justify-between">
        <Link to="/" data-testid="public-lib-brand" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-md grid place-items-center font-display font-bold text-white text-sm">
            AJ
          </div>
          <div className="font-display text-base leading-none">Ad Journal Generator</div>
        </Link>
        {user ? (
          <Button
            data-testid="public-lib-import-btn"
            onClick={importToMine}
            disabled={importing}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <Download className="w-4 h-4 mr-2" />
            {importing ? "Adding…" : "Add to my libraries"}
          </Button>
        ) : (
          <Button
            data-testid="public-lib-signin-btn"
            onClick={() => nav(`/login?redirect=${encodeURIComponent(`/share/library/${shareId}`)}`)}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Sign in to copy
          </Button>
        )}
      </header>

      <main className="max-w-3xl mx-auto p-8">
        <div className="flex items-center gap-3 mb-1">
          <LibraryIcon className="w-7 h-7 text-blue-400" />
          <h1 data-testid="public-lib-name" className="text-3xl font-display font-bold">{lib.name}</h1>
        </div>
        {lib.description && (
          <p className="text-zinc-400 text-sm mb-6">{lib.description}</p>
        )}
        <div className="text-[11px] text-zinc-500 mb-6 font-mono uppercase tracking-wider">
          {(lib.templates || []).length} template{(lib.templates || []).length === 1 ? "" : "s"}
          {" · "}
          {vars.length} variable{vars.length === 1 ? "" : "s"}
          {" · shared read-only"}
        </div>

        {vars.length > 0 && (
          <section className="mb-8">
            <div className="label-eyebrow mb-2">Default variables</div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-md divide-y divide-zinc-800">
              {vars.map(([k, v]) => (
                <div key={k} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="font-mono text-blue-300 min-w-[120px]">{k}</span>
                  <span className="text-zinc-300 truncate">{v || <em className="text-zinc-600">(unset)</em>}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="label-eyebrow mb-2">Templates</div>
          <ol data-testid="public-lib-templates" className="space-y-2">
            {(lib.templates || []).map((t, i) => (
              <li
                key={i}
                className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2.5 text-sm leading-relaxed"
              >
                <span className="text-zinc-600 font-mono mr-3">{i + 1}.</span>
                {t}
              </li>
            ))}
          </ol>
        </section>

        <p className="mt-10 text-xs text-zinc-600 text-center">
          Shared via Ad Journal Generator · use “Add to my libraries” to reuse these templates in your own journals.
        </p>
      </main>
    </div>
  );
}
